import { FC, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import { EvBodyDefault, EvFlexColumn, EvSection, TEXT_COLOR } from '@evinced-private/ui-common';

import {
	MFA_SESSION_SCANS_INCLUDE_ZERO_ISSUE_TOGGLE_DAID,
	MFA_SESSION_SCANS_TAB_DAID,
	MFA_SESSION_SCANS_TABLE_DAID
} from 'data-automation-ids/MfaDashboardAutomationIds';
import { MfaSingleSessionFiltersSection } from 'src/apps/mfa/pages/single-session/filters-section/MfaSingleSessionFiltersSection';
import {
	MobileErrorMessage,
	MobileErrorType
} from 'src/apps/mobile-common/components/mobile-error-message/MobileErrorMessage';
import { useMobileScansListQuery } from 'src/apps/mobile-common/hooks/useMobileScansListQuery';
import { IMobileScansListRow } from 'src/apps/mobile-common/services/mobile-scans-list';
import { EMPTY_SCANS_SUMMARY } from 'src/apps/mobile-common/services/mobile-scans-list/MobileScansList.constants';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import {
	MFA_SESSION_ALL_ISSUES_TABLE_ID,
	MFA_SESSION_SCANS_TABLE_ID
} from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import { MFA_SESSION_SCANS_LABELS } from './MfaSessionScans.labels';
import { IMfaSessionScansProps } from './MfaSessionScans.types';
import { getMfaSessionScansColumns } from './MfaSessionScansTableHelper';

import './MfaSessionScans.scss';

export const MfaSessionScans: FC<IMfaSessionScansProps> = ({ sessionId }) => {
	const history = useHistory();
	const { getTableStateById, onFilterChange } = useTableState();

	const tableId = useMemo(
		() => buildFullTableId(MFA_SESSION_SCANS_TABLE_ID, sessionId),
		[sessionId]
	);
	const issuesTableId = useMemo(
		() => buildFullTableId(MFA_SESSION_ALL_ISSUES_TABLE_ID, sessionId),
		[sessionId]
	);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const scansQuery = useMobileScansListQuery({
		runId: sessionId,
		productName: ProductType.MOBILE_FLOW_ANALYZER,
		includeScansWithoutIssues: filters.includeScansWithoutIssues ?? false,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		filterOptions: filters
	});

	const handleScanNameClick = useCallback(
		(scan: IMobileScansListRow): void => {
			onFilterChange(issuesTableId, {
				scans: [{ value: scan.scanId, label: scan.scanName }]
			});
		},
		[issuesTableId, onFilterChange]
	);

	const columns = useMemo(
		() => getMfaSessionScansColumns(handleScanNameClick, history.location?.pathname),
		[handleScanNameClick, history.location?.pathname]
	);

	if (scansQuery.isError) {
		return (
			<MobileErrorMessage
				errorType={MobileErrorType.LOADING_ERROR}
				productType={ProductType.MOBILE_FLOW_ANALYZER}
			/>
		);
	}

	const { rows = [], total = 0, summary = EMPTY_SCANS_SUMMARY } = scansQuery.data ?? {};
	const isTableLoading = scansQuery.isLoading || scansQuery.isPreviousData;

	return (
		<EvSection
			dataAutomationId={MFA_SESSION_SCANS_TAB_DAID}
			ariaLabel={MFA_SESSION_SCANS_LABELS.SECTION_ARIA_LABEL}
		>
			<EvFlexColumn padding={['MEDIUM', 'NONE', 'NONE', 'NONE']}>
				<MfaSingleSessionFiltersSection
					sessionId={sessionId}
					filters={filters}
					ariaLabel={MFA_SESSION_SCANS_LABELS.FILTERS_ARIA_LABEL}
					onFilterChange={onFilterChange}
					tableId={tableId}
					showIncludeZeroIssueToggle
					includeZeroIssueToggleDataAutomationId={MFA_SESSION_SCANS_INCLUDE_ZERO_ISSUE_TOGGLE_DAID}
				/>
			</EvFlexColumn>
			<EvSection ariaLabel={MFA_SESSION_SCANS_LABELS.TABLE_ARIA_LABEL} skipLinkId="main-section">
				<PlatformTable
					dataAutomationId={MFA_SESSION_SCANS_TABLE_DAID}
					isTableLoading={isTableLoading}
					tableId={tableId}
					tableData={rows}
					totalCount={total}
					totalPaginationableResults={total}
					className="mfa-session-scans__table"
					persistTableState
					columns={columns}
					topRowActions={
						<EvBodyDefault color={TEXT_COLOR.CONTENT_3}>
							{MFA_SESSION_SCANS_LABELS.ISSUES_ELEMENTS_SUMMARY(summary)}
						</EvBodyDefault>
					}
					alignActionsRight={false}
					options={{
						keyField: 'scanId',
						title: MFA_SESSION_SCANS_LABELS.TABLE_TITLE_WITH_SUMMARY(summary),
						caption: MFA_SESSION_SCANS_LABELS.TABLE_CAPTION,
						pagination: true,
						dataType: 'scan',
						remote: true
					}}
					withUrlSwitcher={false}
				/>
			</EvSection>
		</EvSection>
	);
};
